import { Layout } from "@jambushrushti/components/layout/Layout";
import { Seo } from "@jambushrushti/components/shared/Seo";
import { Card, CardContent } from "@jambushrushti/components/ui/card";
import { Button } from "@jambushrushti/components/ui/button";
import { Link } from "react-router-dom";
import {
  ArrowRight,
  BookOpen,
  Globe,
  Award,
  Quote,
  Scroll,
  Calendar,
  MapPin,
  Users,
  Image,
} from "lucide-react";
import { gurudevBio, galleryImages } from "@jambushrushti/data/gurudevData";

const milestones = [
  {
    year: "1923",
    event:
      "Born as Chunilal Bhogilal Joitram in Mandal, Gujarat into a deeply religious family",
  },
  {
    year: "1946",
    event:
      "Initiation into monkhood (Diksha) in the Tapa Gaccha order of Shwetambar Jainism",
  },
  {
    year: "1950s",
    event: "Began intensive study of ancient texts and manuscripts",
  },
  {
    year: "1960s",
    event: "Started extensive manuscript research and preservation work",
  },
  { year: "1970s", event: "Published critical editions of Jain Agamas" },
  {
    year: "1980s",
    event: "Received international recognition for scholarly contributions",
  },
  {
    year: "1990s",
    event: "Continued teaching and mentoring new generation of scholars",
  },
  {
    year: "2009",
    event:
      "Attained Samadhi, leaving behind an unparalleled legacy of scholarship",
  },
];

const achievements = [
  {
    icon: BookOpen,
    title: "Scholarly Works",
    description:
      "Authored and edited over 50 critical editions of Jain Agamas and philosophical texts including Sthananga Sutra, Anuyogdwar Sutra, and Dvadasharam Naychakram",
  },
  {
    icon: Globe,
    title: "Languages Mastered",
    description:
      "Proficient in Sanskrit, Prakrit, Ardhamagadhi, Gujarati, Hindi, and other classical languages essential for Jain scriptural studies",
  },
  {
    icon: Award,
    title: "Manuscript Preservation",
    description:
      "Catalogued and preserved thousands of rare Jain manuscripts across India, including the comprehensive Hastlikhit Granthsuchi series",
  },
];

const familyInfo = {
  father: { name: gurudevBio.fatherName, years: gurudevBio.fatherYears },
  mother: { name: gurudevBio.motherName, years: gurudevBio.motherYears },
};

const AboutGurudev = () => {
  return (
    <Layout>
      <Seo
        title="About Gurudev Muni Jambuvijayaji Maharaj Saheb"
        description="Explore the life, teachings, and legacy of Gurudev Muni Jambuvijayaji Maharaj Saheb (1923-2009) - a revered Jain saint, scholar, and pioneer in manuscript preservation."
      />
      {/* Hero */}
      <section className="bg-gradient-hero lotus-pattern py-5 sm:py-8 lg:py-14">
        <div className="container mx-auto px-4">
          <div className="mx-auto max-w-6xl">
            <Link
              to="/about"
              className="mb-3 inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-primary sm:mb-6"
            >
              ← Back to About
            </Link>

            <div className="grid items-center gap-8 lg:grid-cols-2 lg:gap-12">
              <div className="animate-fade-up">
                <p className="font-heading mb-1 text-base text-primary sm:mb-2 sm:text-lg">
                  {gurudevBio.hindiName}
                </p>
                <h1 className="font-heading mb-3 text-3xl font-bold text-foreground sm:mb-6 sm:text-5xl">
                  Gurudev Muni Jambuvijayaji Maharaj Saheb
                </h1>
                <p className="font-body mb-4 text-base text-muted-foreground sm:mb-6 sm:text-lg">
                  {gurudevBio.shortBio}
                </p>

                {/* Key Facts */}
                <div className="mb-8 grid grid-cols-2 gap-4">
                  <div className="flex items-start gap-3">
                    <MapPin className="mt-0.5 h-5 w-5 text-primary" />
                    <div>
                      <p className="font-body text-base text-muted-foreground">
                        Birth Place
                      </p>
                      <p className="font-body font-medium text-foreground">
                        {gurudevBio.birthPlace}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <Calendar className="mt-0.5 h-5 w-5 text-primary" />
                    <div>
                      <p className="font-body text-base text-muted-foreground">
                        Life Span
                      </p>
                      <p className="font-body font-medium text-foreground">
                        {gurudevBio.birthYear}–{gurudevBio.deathYear}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <Users className="mt-0.5 h-5 w-5 text-primary" />
                    <div>
                      <p className="font-body text-base text-muted-foreground">
                        Order
                      </p>
                      <p className="font-body text-base font-medium text-foreground">
                        {gurudevBio.order}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <BookOpen className="mt-0.5 h-5 w-5 text-primary" />
                    <div>
                      <p className="font-body text-base text-muted-foreground">
                        Birth Name
                      </p>
                      <p className="font-body font-medium text-foreground">
                        {gurudevBio.birthName}
                      </p>
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-2.5 sm:flex sm:flex-wrap sm:gap-4">
                  <Button
                    variant="spiritual"
                    className="w-full px-2.5 text-xs sm:w-auto sm:px-5 sm:text-sm"
                    asChild
                  >
                    <Link to="/guruvani" className="justify-center">
                      Explore Guruvani
                      <Scroll className="ml-1.5 h-3.5 w-3.5 shrink-0 sm:ml-2 sm:h-4 sm:w-4" />
                    </Link>
                  </Button>
                  <Button
                    variant="outline"
                    className="w-full border-[#D5D0C5] bg-white px-2.5 text-xs text-[#2D2A26] transition-all hover:border-[#2D2A26] hover:bg-[#2D2A26] hover:text-white sm:w-auto sm:px-5 sm:text-sm"
                    asChild
                  >
                    <Link to="/gallery" className="justify-center">
                      Photo Gallery
                      <Image className="ml-1.5 h-3.5 w-3.5 shrink-0 sm:ml-2 sm:h-4 sm:w-4" />
                    </Link>
                  </Button>
                </div>
              </div>

              {/* Gurudev's Photo */}
              <div className="animate-fade-up delay-200">
                <div className="shadow-elevated relative overflow-hidden rounded-2xl">
                  <img
                    src={gurudevBio.mainImage}
                    alt="Gurudev Muni Jambuvijayaji Maharaj Saheb in white robes sitting cross-legged"
                    className="h-auto w-full"
                  />
                  <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-background/90 to-transparent p-6">
                    <p className="font-body text-base text-muted-foreground">
                      Gurudev Muni Jambuvijayaji Maharaj Saheb
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Family Background */}
      <section className="bg-secondary/30 py-4 sm:py-6">
        <div className="container mx-auto px-4">
          <div className="mx-auto max-w-3xl">
            <h2 className="font-heading mb-4 text-center text-xl font-bold text-foreground sm:mb-6 sm:text-2xl">
              Family Background
            </h2>
            <div className="grid gap-4 sm:gap-6 md:grid-cols-2">
              <Card variant="default">
                <CardContent className="p-4 text-center sm:p-6">
                  <p className="font-body mb-1 text-sm text-muted-foreground sm:text-base">
                    Father
                  </p>
                  <p className="font-heading font-semibold text-foreground">
                    {familyInfo.father.name}
                  </p>
                  <p className="font-body text-sm text-muted-foreground sm:text-base">
                    ({familyInfo.father.years})
                  </p>
                </CardContent>
              </Card>
              <Card variant="default">
                <CardContent className="p-4 text-center sm:p-6">
                  <p className="font-body mb-1 text-sm text-muted-foreground sm:text-base">
                    Mother
                  </p>
                  <p className="font-heading font-semibold text-foreground">
                    {familyInfo.mother.name}
                  </p>
                  <p className="font-body text-sm text-muted-foreground sm:text-base">
                    ({familyInfo.mother.years})
                  </p>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Quote */}
      <section className="bg-burgundy/5 py-4 sm:py-6">
        <div className="container mx-auto px-4">
          <div className="mx-auto max-w-3xl text-center">
            <Quote className="text-burgundy/30 mx-auto mb-3 h-8 w-8 sm:h-10 sm:w-10" />
            <blockquote className="font-heading mb-3 text-lg italic text-foreground sm:mb-4 sm:text-2xl">
              "True knowledge illuminates the path of liberation. Through
              rigorous study and unwavering devotion, one attains the wisdom
              that transcends worldly bonds."
            </blockquote>
            <p className="font-body text-sm text-muted-foreground sm:text-base">
              — Gurudev Muni Jambuvijayaji
            </p>
          </div>
        </div>
      </section>

      {/* Life Journey */}
      <section className="bg-background py-6 sm:py-10 lg:py-14">
        <div className="container mx-auto px-4">
          <h2 className="font-heading mb-6 text-center text-2xl font-bold text-foreground sm:mb-8 sm:text-3xl">
            Life Journey
          </h2>

          <div className="mx-auto max-w-3xl">
            <div className="relative">
              {/* Timeline line */}
              <div className="absolute bottom-0 left-8 top-0 w-0.5 bg-border" />

              {milestones.map((milestone, index) => (
                <div
                  key={milestone.year}
                  className="animate-fade-up relative pb-8 pl-20"
                  style={{ animationDelay: `${index * 100}ms` }}
                >
                  <div className="absolute left-4 flex h-8 w-8 items-center justify-center rounded-full bg-primary text-sm font-medium text-primary-foreground">
                    <Calendar className="h-4 w-4" />
                  </div>
                  <Card variant="default">
                    <CardContent className="p-4">
                      <p className="font-body mb-1 text-base font-medium text-primary">
                        {milestone.year}
                      </p>
                      <p className="font-body text-base text-foreground">
                        {milestone.event}
                      </p>
                    </CardContent>
                  </Card>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Achievements */}
      <section className="bg-gradient-spiritual py-6 sm:py-10 lg:py-14">
        <div className="container mx-auto px-4">
          <h2 className="font-heading mb-6 text-center text-2xl font-bold text-foreground sm:mb-8 sm:text-3xl">
            Contributions & Achievements
          </h2>

          <div className="mx-auto grid max-w-5xl gap-6 sm:gap-8 md:grid-cols-3">
            {achievements.map((item, index) => (
              <Card
                key={item.title}
                variant="feature"
                className="animate-fade-up"
                style={{ animationDelay: `${index * 100}ms` }}
              >
                <CardContent className="p-6 text-center">
                  <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-2xl bg-primary/10">
                    <item.icon className="h-8 w-8 text-primary" />
                  </div>
                  <h3 className="font-heading mb-3 text-xl font-semibold text-foreground">
                    {item.title}
                  </h3>
                  <p className="font-body text-base text-muted-foreground">
                    {item.description}
                  </p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Photo Gallery Preview */}
      <section className="bg-background py-6 sm:py-10 lg:py-14">
        <div className="container mx-auto px-4">
          <div className="mb-6 flex items-center justify-between sm:mb-8">
            <h2 className="font-heading text-xl font-bold text-foreground sm:text-2xl">
              Photo Gallery
            </h2>
            <Button variant="outline" asChild>
              <Link to="/gallery">
                View All Photos
                <ArrowRight className="ml-2 h-4 w-4" />
              </Link>
            </Button>
          </div>

          <div className="grid grid-cols-2 gap-4 md:grid-cols-4 lg:grid-cols-6">
            {galleryImages.slice(0, 6).map((image, index) => (
              <Link
                key={index}
                to="/gallery"
                className="shadow-soft hover:shadow-elevated group aspect-square overflow-hidden rounded-xl transition-all"
              >
                <img
                  src={image.thumb}
                  alt={image.alt || `Gurudev Photo ${index + 1}`}
                  className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-110"
                />
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-secondary/30 py-6 sm:py-10 lg:py-14">
        <div className="container mx-auto px-4 text-center">
          <h2 className="font-heading mb-4 text-2xl font-bold text-foreground">
            Continue Exploring
          </h2>
          <div className="flex flex-wrap justify-center gap-4">
            <Button variant="outline" asChild>
              <Link to="/about/parivar">
                Legacy of Pujyapad Muni Jambuvijayaji Maharaj
                <ArrowRight className="ml-2 h-4 w-4" />
              </Link>
            </Button>
            <Button variant="outline" asChild>
              <Link to="/about/gyan-kendra">
                Research Center
                <ArrowRight className="ml-2 h-4 w-4" />
              </Link>
            </Button>
            <Button variant="hero" asChild>
              <Link to="/guruvani">
                Access Guruvani
                <Scroll className="ml-2 h-4 w-4" />
              </Link>
            </Button>
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default AboutGurudev;
